import { clearBodyScrollLock, useClearBodyScrollLock } from '@/components/Functional/utils/clearBodyScrollLock'
import { Button } from '@/components/UI/Button'
import { Dialog } from '@/components/UI/Dialog'
import { HtmlContent } from '@/components/UI/HtmlContent'
import { ActionIcon } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import type { Result } from '@zxing/library'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Scanner, { type ScanMode } from './Scanner'

export type ScanCodeOverlayProps = {
  /** オーバーレイの開閉状態 */
  opened: boolean
  /** 閉じる時のコールバック（✕ボタン押下・ブラウザバック） */
  onClose: () => void
  /**
   * コード読み取り成功時のコールバック
   * @param result - ZXing の Result オブジェクト（result.getText() でコード文字列を取得）
   * @returns true を返すとカメラを停止してオーバーレイを閉じる
   */
  onRead: (result: Result) => boolean | Promise<boolean>
  /** aria-label に使用するタイトル（未指定の場合は mode に応じたデフォルトを使用） */
  title?: string
  /**
   * 読み取りモード
   * - 'qr': QRコード専用（デフォルト）
   * - 'barcode': バーコード含む全フォーマット対応（ガイドライン自動表示）
   */
  mode?: ScanMode
}

const DEFAULT_TITLES: Record<ScanMode, string> = {
  qr: 'QRコードを読み取る',
  barcode: 'バーコードを読み取る',
}

export const ScanCodeOverlay = memo(({ opened, onClose, onRead, title, mode = 'qr' }: ScanCodeOverlayProps) => {
  const { t } = useTranslation()
  const overlayRef = useRef<HTMLDivElement | null>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  useClearBodyScrollLock()
  const [cameraPermissionDenied, setCameraPermissionDenied] = useState(false)

  const displayTitle = title ?? DEFAULT_TITLES[mode]

  // body-scroll-lock 制御
  useEffect(() => {
    if (!opened) return
    const el = overlayRef.current
    if (!el) return
    disableBodyScroll(el)
    return () => {
      enableBodyScroll(el)
      clearBodyScrollLock()
    }
  }, [opened])

  // ブラウザの戻るボタンでオーバーレイを閉じる
  useEffect(() => {
    if (!opened) return

    window.history.pushState({ scanCodeOverlay: true }, '')

    const handlePopState = () => {
      onCloseRef.current()
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
      if (window.history.state?.scanCodeOverlay) {
        window.history.back()
      }
    }
  }, [opened])

  const handleRead = useCallback(
    async (result: Result) => {
      const shouldClose = await onRead(result)
      if (shouldClose) onClose()
      return shouldClose
    },
    [onRead, onClose],
  )

  const handleCameraPermissionDenied = useCallback(() => {
    setCameraPermissionDenied(true)
  }, [])

  const handlePermissionDialogClose = useCallback(() => {
    setCameraPermissionDenied(false)
    onClose()
  }, [onClose])

  const cameraPermissionDeniedDescription =
    mode === 'qr'
      ? t(
          'scanner.camera_permission_denied.description_qr',
          'QRコードを読み込むにはカメラの許可が必要です。<br>ブラウザの設定からカメラの許可をしてください。',
        )
      : t(
          'scanner.camera_permission_denied.description_barcode',
          'バーコードを読み込むにはカメラの許可が必要です。<br>ブラウザの設定からカメラの許可をしてください。',
        )

  if (!opened) return null

  return (
    <>
      <div
        ref={overlayRef}
        className="fixed inset-0 z-[1000] bg-black"
        role="dialog"
        aria-label={displayTitle}
        aria-hidden={cameraPermissionDenied}
      >
        {/* カメラ映像エリア（フルスクリーン） */}
        <div className="relative h-full w-full">
          <Scanner
            ready={opened}
            mode={mode}
            guide={mode === 'barcode'}
            onRead={handleRead}
            onCameraPermissionDenied={handleCameraPermissionDenied}
          />
        </div>
        {/* フローティング✕ボタン（右上） */}
        <div className="absolute right-4 top-4 z-[100]">
          <ActionIcon
            variant="subtle"
            color="white"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
            onClick={() => window.history.back()}
            aria-label={t('common.close', '閉じる')}
          >
            <IconX size={24} stroke={1.5} aria-hidden="true" />
          </ActionIcon>
        </div>
      </div>

      {/* カメラ許可エラーダイアログ */}
      <Dialog
        selectorState={cameraPermissionDenied}
        title={t('scanner.camera_permission_denied.title', 'カメラの許可が必要です')}
        zIndex={1100}
        compact
        showCloseButton={false}
        closeAction={handlePermissionDialogClose}
      >
        <div>
          <HtmlContent
            html={cameraPermissionDeniedDescription}
            className="text-gray-600 mt-5 text-center text-sm font-bold"
          />
          <div className="c-dialog-button-area c-dialog-button-area--single">
            <Button
              label={t('common.close', '閉じる')}
              variant="system"
              size="md"
              round
              onClick={handlePermissionDialogClose}
            />
          </div>
        </div>
      </Dialog>
    </>
  )
})

ScanCodeOverlay.displayName = 'ScanCodeOverlay'
